'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { SUPPORTED_LANGUAGES } from '@/lib/i18n/i18n';
import { useLanguage } from '@/contexts/LanguageContext';

interface LanguageSelectorScreenProps {
  onLanguageSelected: () => void;
}

export default function LanguageSelectorScreen({ onLanguageSelected }: LanguageSelectorScreenProps) {
  const { languageCode, setLanguageByCode } = useLanguage();
  const [selected, setSelected] = useState(languageCode || 'en');

  const handleConfirm = () => {
    setLanguageByCode(selected);
    onLanguageSelected();
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12" style={{ backgroundColor: '#f5f0e8' }}>
      {/* Logo */}
      <div className="flex flex-col items-center mb-8">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mb-4 shadow-lg" style={{ backgroundColor: '#0f0f0f' }}>
          <Image
            src="/assets/images/app_logo.png"
            alt="Fanxiety Club logo"
            width={48}
            height={48}
            className="rounded-xl object-contain"
          />
        </div>
        <h1 className="text-2xl font-black tracking-tight" style={{ color: '#0f0f0f' }}>
          Fanxiety <span style={{ color: '#d4c800' }}>Club</span>
        </h1>
      </div>

      {/* Card */}
      <div className="w-full max-w-md rounded-2xl shadow-xl overflow-hidden" style={{ backgroundColor: '#fff', border: '1px solid #e8e0d0' }}>
        <div className="px-6 pt-6 pb-4" style={{ backgroundColor: '#0f0f0f' }}>
          <p className="text-xs font-bold uppercase tracking-widest mb-1" style={{ color: '#d4c800' }}>
            🌍 Choose your language
          </p>
          <h2 className="text-xl font-black text-white">
            How do you want to experience the club?
          </h2>
          <p className="text-sm mt-1" style={{ color: '#a09880' }}>
            The full app will be translated for you.
          </p>
        </div>

        <div className="px-6 py-4">
          {/* Language list */}
          <div className="space-y-2">
            {SUPPORTED_LANGUAGES.map(({ code, label, name }) => {
              const isSelected = selected === code;
              return (
                <button
                  key={code}
                  type="button"
                  onClick={() => setSelected(code)}
                  className="w-full flex items-center gap-3 px-4 py-3.5 rounded-xl text-left transition-all duration-150 text-sm font-semibold"
                  style={
                    isSelected
                      ? { backgroundColor: '#0f0f0f', color: '#f0e840', border: '2px solid #0f0f0f' }
                      : { backgroundColor: '#f5f0e8', color: '#0f0f0f', border: '2px solid transparent' }
                  }
                >
                  <span className="text-2xl leading-none">{label.split(' ')[0]}</span>
                  <div className="flex-1">
                    <p className="font-black">{name}</p>
                    <p className="text-xs opacity-60">{label.split(' ').slice(1).join(' ')}</p>
                  </div>
                  {isSelected && (
                    <span className="w-5 h-5 rounded-full flex items-center justify-center text-xs font-black" style={{ backgroundColor: '#f0e840', color: '#0f0f0f' }}>✓</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="px-6 pb-6">
          <button
            type="button"
            onClick={handleConfirm}
            className="w-full py-3.5 rounded-xl text-sm font-black transition-all duration-150 active:scale-95"
            style={{ backgroundColor: '#0f0f0f', color: '#f0e840' }}
          >
            ⚽ Continue in {SUPPORTED_LANGUAGES.find(l => l.code === selected)?.name ?? 'English'}
          </button>
          <p className="text-xs text-center mt-3 font-medium" style={{ color: '#8a7a60' }}>
            You can change this later in Settings
          </p>
        </div>
      </div>
    </div>
  );
}
